/**
 * useTooltipPortal Hook
 *
 * Charter: documentation/cursor/Charters/CodeAuditor.md v1.0.1
 *
 * Purpose:
 *   Manages tooltip visibility, content, and viewport-relative position for a portaled
 *   tooltip (e.g. Owner Payout info-icon). Call showTooltip(e, content) on hover/focus and
 *   hideTooltip() on mouseleave/blur/click-outside. Positioning math is identical to the
 *   previous inline implementation (getBoundingClientRect, space above/below, gap/height constants).
 *
 * Workflow:
 *   1. Initialize state: { show: false, content: '', x: 0, y: 0, positionAbove: true }.
 *   2. showTooltip(e, content): Compute rect from e.currentTarget.getBoundingClientRect();
 *      tooltipHeight 60, tooltipGap 8; spaceAbove = rect.top, spaceBelow = viewportBottom - rect.bottom;
 *      positionAbove = spaceAbove > tooltipHeight + tooltipGap; x = rect.left + rect.width/2;
 *      y = positionAbove ? rect.top - tooltipGap : rect.bottom + tooltipGap; set state.
 *   3. hideTooltip(): Set state to show: false, content: '', x: 0, y: 0, positionAbove: true.
 *   4. Return { tooltipState, showTooltip, hideTooltip } for caller to render TooltipPortal and wire handlers.
 *
 * Assumptions:
 *   - Tooltip is rendered via createPortal(..., document.body). Caller is responsible for portaling.
 *   - No AbortController or cleanup beyond state; caller wires hideTooltip on mouseleave/blur/click-outside.
 *
 * Dependencies:
 *   - React (useState). No external libs.
 *
 * Version: 1.0.0
 *
 * Changelog:
 *   2026-01-27 Phase 6d: extracted from BookingDetailModal.jsx; positioning math and state logic moved verbatim.
 */

import { useState, useCallback, useRef } from 'react';

const INITIAL_STATE = { show: false, content: '', x: 0, y: 0, positionAbove: true };
const HIDE_DELAY_MS = 300; // Allow moving from icon to tooltip to click link

export function useTooltipPortal() {
  const [tooltipState, setTooltipState] = useState(INITIAL_STATE);
  const hideTimeoutRef = useRef(null);

  const showTooltip = useCallback((e, content) => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    // Position tooltip adjacent to icon element
    // Check if there's enough space above; if not, position below
    const tooltipHeight = 60; // Approximate tooltip height (with padding)
    const tooltipGap = 8; // Gap between icon and tooltip

    // Get viewport dimensions (account for scroll)
    const viewportTop = window.scrollY || 0;
    const viewportBottom = window.scrollY + window.innerHeight;
    const spaceAbove = rect.top;
    const spaceBelow = viewportBottom - rect.bottom;
    const positionAbove = spaceAbove > tooltipHeight + tooltipGap;

    // Calculate position: center horizontally on icon, vertical based on space
    // Use getBoundingClientRect() which gives viewport-relative coordinates
    // Since tooltip is portaled to body with position: fixed, these coords are correct
    const x = rect.left + rect.width / 2;
    const y = positionAbove
      ? rect.top - tooltipGap  // Above: icon top minus gap (viewport coords)
      : rect.bottom + tooltipGap;  // Below: icon bottom plus gap (viewport coords)

    // DEBUG: Log coordinate calculations
    const tooltipDebug = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('tooltip_debug') === '1';
    if (tooltipDebug) {
      const modalElement = e.currentTarget.closest('[role="dialog"]');
      const scrollContainer = modalElement?.querySelector('[style*="overflow"]');
      console.log('[Tooltip Debug] showTooltip:', {
        triggerRect: {
          top: rect.top,
          left: rect.left,
          bottom: rect.bottom,
          right: rect.right,
          width: rect.width,
          height: rect.height,
        },
        viewport: {
          scrollY: window.scrollY,
          innerHeight: window.innerHeight,
          innerWidth: window.innerWidth,
        },
        position: {
          x,
          y,
          positionAbove,
          spaceAbove,
          spaceBelow,
        },
        modalElement: modalElement ? {
          tagName: modalElement.tagName,
          hasTransform: window.getComputedStyle(modalElement).transform !== 'none',
        } : null,
        scrollContainer: scrollContainer ? {
          scrollTop: scrollContainer.scrollTop,
          scrollHeight: scrollContainer.scrollHeight,
        } : null,
      });
    }

    setTooltipState({
      show: true,
      content,
      x,
      y,
      positionAbove, // Store position direction for transform/arrow
    });
  }, []);

  const hideTooltip = useCallback(() => {
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    hideTimeoutRef.current = setTimeout(() => {
      hideTimeoutRef.current = null;
      setTooltipState(INITIAL_STATE);
    }, HIDE_DELAY_MS);
  }, []);

  const cancelHideTooltip = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  return { tooltipState, showTooltip, hideTooltip, cancelHideTooltip };
}
